import { resourceNameKeyValues } from "motw_core/Entity/IKeyValue";
import {
    GetKeyValueJsonAsync,
    SetKeyValueJsonAsync,
} from "source/app/infrastructure/Data/KeyValues/KeyValueJson";
import ClientDataProvider from "source/app/services/External/ClientDataProvider/ClientDataProvider";
import {
    BuildQuickJournalStoredSnapshot,
    MergeQuickJournalHourlyCountMaps,
} from "../Stats/QuickJournalStatsCore";
import {
    IQuickJournalEntry,
    IQuickJournalStoredSnapshot,
    quickJournalRecentEntriesStorageKey,
    quickJournalSyncedEntriesKey,
} from "../QuickJournalTypes";
import { MergeRecentEntries } from "./QuickJournalEntryNormalization";

export function LoadQuickJournalStoredSnapshotFromLocalStorage(): IQuickJournalStoredSnapshot {
    if (typeof localStorage === "undefined") {
        return BuildQuickJournalStoredSnapshot([]);
    }

    try {
        const serializedEntries = localStorage.getItem(
            quickJournalRecentEntriesStorageKey
        );

        if (!serializedEntries) {
            return BuildQuickJournalStoredSnapshot([]);
        }

        return ParseQuickJournalStoredSnapshot(serializedEntries);
    } catch {
        return BuildQuickJournalStoredSnapshot([]);
    }
}

export function ShouldWipeQuickJournalData(): boolean {
    if (typeof window === "undefined") {
        return false;
    }

    return new URLSearchParams(window.location.search).has("quickJournalWipe");
}

export async function WipeQuickJournalDataAsync(): Promise<void> {
    if (typeof localStorage !== "undefined") {
        localStorage.removeItem(quickJournalRecentEntriesStorageKey);
    }

    try {
        await ClientDataProvider.DeleteRecordsAsync(
            `${resourceNameKeyValues}?key=eq.${encodeURIComponent(
                quickJournalSyncedEntriesKey
            )}`
        );
    } catch {
        // Best-effort wipe in dev.
    }
}

export function SaveQuickJournalSnapshotToLocalStorage(
    snapshot: IQuickJournalStoredSnapshot
): IQuickJournalStoredSnapshot {
    const normalizedSnapshot = BuildQuickJournalStoredSnapshot(
        snapshot.recentEntries,
        snapshot.weeklyHourlyCounts
    );

    if (typeof localStorage === "undefined") {
        return normalizedSnapshot;
    }

    localStorage.setItem(
        quickJournalRecentEntriesStorageKey,
        GetEntriesStorageValue(normalizedSnapshot)
    );

    return normalizedSnapshot;
}

export async function SaveQuickJournalSnapshotToServerAsync(
    localSnapshot: IQuickJournalStoredSnapshot
): Promise<IQuickJournalStoredSnapshot> {
    const remoteSnapshot =
        await LoadQuickJournalStoredSnapshotFromServerAsync();
    const entriesToStore = MergeRecentEntries(
        localSnapshot.recentEntries,
        remoteSnapshot.recentEntries
    );
    const weeklyHourlyCounts = MergeQuickJournalHourlyCountMaps(
        localSnapshot.weeklyHourlyCounts,
        remoteSnapshot.weeklyHourlyCounts
    );
    const snapshotToStore = BuildQuickJournalStoredSnapshot(
        entriesToStore,
        weeklyHourlyCounts
    );
    const value = GetEntriesStorageValue(snapshotToStore);

    try {
        await SetKeyValueJsonAsync(
            quickJournalSyncedEntriesKey,
            snapshotToStore,
            { expiry: 0, legacyValue: value }
        );
    } catch {
        return snapshotToStore;
    }

    return snapshotToStore;
}

export async function LoadQuickJournalStoredSnapshotFromServerAsync(): Promise<IQuickJournalStoredSnapshot> {
    let storedSnapshot:
        | IQuickJournalStoredSnapshot
        | IQuickJournalEntry[]
        | string
        | undefined;

    try {
        storedSnapshot = await GetKeyValueJsonAsync<
            IQuickJournalStoredSnapshot | IQuickJournalEntry[] | string
        >(quickJournalSyncedEntriesKey);
    } catch {
        return BuildQuickJournalStoredSnapshot([]);
    }

    if (!storedSnapshot) {
        return BuildQuickJournalStoredSnapshot([]);
    }

    try {
        if (typeof storedSnapshot === "string") {
            return ParseQuickJournalStoredSnapshot(storedSnapshot);
        }

        return ParseQuickJournalStoredSnapshotValue(storedSnapshot);
    } catch {
        return BuildQuickJournalStoredSnapshot([]);
    }
}

export function GetEntriesStorageValue(
    snapshot: IQuickJournalStoredSnapshot
): string {
    return JSON.stringify(snapshot);
}

function ParseQuickJournalStoredSnapshot(
    serializedValue: string
): IQuickJournalStoredSnapshot {
    return ParseQuickJournalStoredSnapshotValue(JSON.parse(serializedValue));
}

function ParseQuickJournalStoredSnapshotValue(
    parsedValue: IQuickJournalStoredSnapshot | IQuickJournalEntry[]
): IQuickJournalStoredSnapshot {
    if (Array.isArray(parsedValue)) {
        return BuildQuickJournalStoredSnapshot(parsedValue);
    }

    if (!parsedValue || typeof parsedValue !== "object") {
        return BuildQuickJournalStoredSnapshot([]);
    }

    const candidate = parsedValue as Partial<IQuickJournalStoredSnapshot>;

    return BuildQuickJournalStoredSnapshot(
        Array.isArray(candidate.recentEntries) ? candidate.recentEntries : [],
        candidate.weeklyHourlyCounts
    );
}
